import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const DuelHistoryCard = ({ duel, userId, index, isExpanded, onToggleExpand }) => {
  const isCreator = duel?.creator_id === userId;
  const opponent = isCreator ? duel?.opponent : duel?.creator;
  const userProgress = isCreator ? duel?.creator_progress : duel?.opponent_progress;
  const opponentProgress = isCreator ? duel?.opponent_progress : duel?.creator_progress;
  const progressPercentage = Math.min((userProgress / duel?.target_value) * 100, 100);
  const opponentProgressPercentage = Math.min((opponentProgress / duel?.target_value) * 100, 100);
  
  const statusConfig = {
    pending: { bg: 'bg-accent/10', border: 'border-accent/30', text: 'text-accent', label: 'Pendiente', icon: 'Clock' },
    active: { bg: 'bg-warning/10', border: 'border-warning/30', text: 'text-warning', label: 'Activo', icon: 'Zap' },
    completed: { bg: 'bg-success/10', border: 'border-success/30', text: 'text-success', label: 'Completado', icon: 'CheckCircle' },
    cancelled: { bg: 'bg-muted/30', border: 'border-muted', text: 'text-muted-foreground', label: 'Cancelado', icon: 'XCircle' }
  };

  const status = statusConfig?.[duel?.status] || statusConfig?.pending;
  
  const metricConfig = {
    gym_workouts: { label: 'Entrenamientos', icon: 'Dumbbell', color: 'text-purple-500', unit: 'sesiones' },
    calories_limit: { label: 'Límite de Calorías', icon: 'Flame', color: 'text-orange-500', unit: 'kcal' },
    sleep_hours: { label: 'Horas de Sueño', icon: 'Moon', color: 'text-blue-500', unit: 'horas' },
    water_intake: { label: 'Hidratación', icon: 'Droplets', color: 'text-cyan-500', unit: 'litros' },
    daily_steps: { label: 'Pasos Diarios', icon: 'Footprints', color: 'text-green-500', unit: 'pasos' },
    daily_minimum: { label: 'Actividad Mínima', icon: 'CheckCircle', color: 'text-primary', unit: 'días' }
  };

  const metric = metricConfig?.[duel?.metric] || { label: duel?.metric, icon: 'Target', color: 'text-primary', unit: '' };

  const isWinner = duel?.winner_id === userId;
  const isLoser = duel?.winner_id && duel?.winner_id !== userId;
  const isDraw = duel?.status === 'completed' && !duel?.winner_id;

  // Calculate duration
  const startDate = duel?.start_date ? new Date(duel.start_date) : null;
  const endDate = duel?.end_date ? new Date(duel.end_date) : null;
  const duration = startDate && endDate ? Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) : duel?.duration_days;

  // Format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date?.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ delay: index * 0.05 }}
      className={`relative bg-card rounded-xl border-2 ${status?.border} hover:shadow-lg transition-all duration-300 overflow-hidden`}
    >
      {/* Main Card Content */}
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          {/* Left: Opponent & Metric Info */}
          <div className="flex items-start space-x-4 flex-1">
            {/* Opponent Avatar */}
            <div className="relative flex-shrink-0">
              {opponent?.avatar_url ? (
                <img
                  src={opponent?.avatar_url}
                  alt={opponent?.full_name}
                  className="w-14 h-14 rounded-full object-cover border-2 border-border"
                />
              ) : (
                <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center border-2 border-border">
                  <span className="text-lg font-semibold text-primary">
                    {opponent?.full_name?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
              )}
              {/* Result Badge on Avatar */}
              {duel?.status === 'completed' && (
                <div className={`absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center ${
                  isWinner ? 'bg-success' : isLoser ? 'bg-error' : 'bg-muted'
                } border-2 border-card`}>
                  <Icon
                    name={isWinner ? 'Trophy' : isLoser ? 'X' : 'Minus'}
                    size={12}
                    color="white"
                  />
                </div>
              )}
            </div>

            {/* Duel Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <h3 className="text-lg font-semibold text-foreground truncate">
                  {duel?.title}
                </h3>
                <Icon name={metric?.icon} size={18} className={metric?.color} />
              </div>
              <p className="text-sm text-muted-foreground mb-2">
                vs <span className="font-medium text-foreground">{opponent?.full_name}</span>
              </p>
              <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <div className="flex items-center space-x-1">
                  <Icon name="Target" size={14} />
                  <span>{metric?.label}</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Icon name="Calendar" size={14} />
                  <span>{duration} días</span>
                </div>
                {startDate && (
                  <div className="flex items-center space-x-1">
                    <Icon name="Clock" size={14} />
                    <span>{formatDate(duel?.start_date)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right: Status Badge */}
          <div className="flex flex-col items-end space-y-2">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${status?.bg} ${status?.text} flex items-center space-x-1`}>
              <Icon name={status?.icon} size={14} />
              <span>{status?.label}</span>
            </span>
            {duel?.status === 'completed' && (
              <span className={`text-xs font-semibold ${
                isWinner ? 'text-success' : isLoser ? 'text-error' : 'text-muted-foreground'
              }`}>
                {isWinner ? '¡Victoria!' : isLoser ? 'Derrota' : 'Empate'}
              </span>
            )}
          </div>
        </div>

        {/* Progress Bars (Compact View) */}
        {(duel?.status === 'active' || duel?.status === 'completed') && (
          <div className="space-y-3 mb-4">
            {/* User Progress */}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Tu Progreso</span>
                <span className={`font-medium ${
                  isWinner ? 'text-success' : 'text-primary'
                }`}>
                  {userProgress} / {duel?.target_value} {metric?.unit}
                </span>
              </div>
              <div className="w-full bg-muted/50 rounded-full h-2">
                <motion.div
                  className={`h-2 rounded-full ${
                    isWinner ? 'bg-success' : 'bg-primary'
                  }`}
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPercentage}%` }}
                  transition={{ duration: 0.8, delay: index * 0.05 + 0.2 }}
                />
              </div>
            </div>

            {/* Opponent Progress */}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{opponent?.full_name}</span>
                <span className={`font-medium ${
                  isLoser ? 'text-success' : 'text-muted-foreground'
                }`}>
                  {opponentProgress} / {duel?.target_value} {metric?.unit}
                </span>
              </div>
              <div className="w-full bg-muted/50 rounded-full h-2">
                <motion.div
                  className={`h-2 rounded-full ${
                    isLoser ? 'bg-success' : 'bg-muted-foreground'
                  }`}
                  initial={{ width: 0 }}
                  animate={{ width: `${opponentProgressPercentage}%` }}
                  transition={{ duration: 0.8, delay: index * 0.05 + 0.3 }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Stake Info */}
        {duel?.stake && (
          <div className={`p-3 rounded-lg ${
            duel?.status === 'completed' && isLoser ? 'bg-error/10 border border-error/30' : 'bg-muted/30'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Icon name={duel?.stake?.icon} size={16} className="text-warning" />
                <span className="text-sm font-medium text-foreground">
                  Stake: {duel?.stake?.title}
                </span>
              </div>
              {duel?.status === 'completed' && isLoser && (
                <span className="text-xs font-semibold text-error">¡Debes cumplir!</span>
              )}
            </div>
          </div>
        )}

        {/* Expand/Collapse Button */}
        <button
          onClick={onToggleExpand}
          className="w-full mt-4 pt-4 border-t border-border flex items-center justify-center space-x-2 text-sm font-medium text-primary hover:text-primary/80 transition-colors"
        >
          <span>{isExpanded ? 'Ver menos' : 'Ver detalles'}</span>
          <Icon
            name="ChevronDown"
            size={16}
            className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          />
        </button>
      </div>
      {/* Expanded Details */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="border-t border-border overflow-hidden"
          >
            <div className="p-6 bg-muted/20 space-y-4">
              {/* Detailed Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 bg-card rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Fecha Inicio</p>
                  <p className="text-sm font-semibold text-foreground">{formatDate(duel?.start_date)}</p>
                </div>
                <div className="p-3 bg-card rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Fecha Fin</p>
                  <p className="text-sm font-semibold text-foreground">{formatDate(duel?.end_date)}</p>
                </div>
                <div className="p-3 bg-card rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Duración</p>
                  <p className="text-sm font-semibold text-foreground">{duration} días</p>
                </div>
                <div className="p-3 bg-card rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Meta</p>
                  <p className="text-sm font-semibold text-foreground">{duel?.target_value} {metric?.unit}</p>
                </div>
              </div>

              {/* Performance Comparison */}
              {(duel?.status === 'completed' || duel?.status === 'active') && (
                <div className="p-4 bg-card rounded-lg">
                  <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center space-x-2">
                    <Icon name="BarChart3" size={16} />
                    <span>Comparación de Rendimiento</span>
                  </h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Tú</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-32 bg-muted/50 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${isWinner ? 'bg-success' : 'bg-primary'}`}
                            style={{ width: `${progressPercentage}%` }}
                          />
                        </div>
                        <span className="text-sm font-semibold text-foreground w-20 text-right">
                          {progressPercentage?.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{opponent?.full_name}</span>
                      <div className="flex items-center space-x-2">
                        <div className="w-32 bg-muted/50 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${isLoser ? 'bg-success' : 'bg-muted-foreground'}`}
                            style={{ width: `${opponentProgressPercentage}%` }}
                          />
                        </div>
                        <span className="text-sm font-semibold text-foreground w-20 text-right">
                          {opponentProgressPercentage?.toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Stake Details */}
              {duel?.stake && (
                <div className="p-4 bg-card rounded-lg">
                  <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center space-x-2">
                    <Icon name={duel?.stake?.icon} size={16} className="text-warning" />
                    <span>Detalles del Stake</span>
                  </h4>
                  <p className="text-sm text-muted-foreground mb-2">{duel?.stake?.description}</p>
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      duel?.stake?.severity === 'light' ? 'bg-green-500/10 text-green-500' :
                      duel?.stake?.severity === 'medium'? 'bg-yellow-500/10 text-yellow-500' : 'bg-red-500/10 text-red-500'
                    }`}>
                      {duel?.stake?.severity === 'light' ? 'Leve' :
                       duel?.stake?.severity === 'medium' ? 'Medio' : 'Picante'}
                    </span>
                    {duel?.status === 'completed' && isLoser && (
                      <span className="text-xs font-semibold text-error">⚠️ Pendiente de cumplir</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default DuelHistoryCard;